/**
 * Main Application Component
 * 
 * This is the root component of the trading application frontend. It sets up
 * the core application structure including routing, authentication, and data fetching.
 * 
 * Key features:
 * - Client-side routing with React Router
 * - Global authentication state management
 * - React Query for server state management and caching
 * - Protected route system for authenticated pages
 * - Public routes that redirect authenticated users
 * 
 * Architecture:
 * - QueryClient: Manages API calls, caching, and data synchronization
 * - AuthProvider: Provides authentication context throughout the app
 * - Router: Handles client-side navigation and URL management
 * - Route guards: Protect sensitive pages and handle redirections
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ModeThemeProvider, ModeThemeBinder } from './contexts/ModeThemeContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AppLayout } from './components/layout/AppLayout';
import { Dashboard } from './pages/Dashboard';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { PositionsPage } from './pages/PositionsPage';
import { AlgorithmTradingPage } from './pages/AlgorithmTradingPage';
import { Settings } from './pages/Settings';
import { DebugPage } from './pages/DebugPage';
import WebhookStocksPage from './pages/WebhookStocksPage';
import NewsPage from './pages/NewsPage';
import { NukePage } from './pages/NukePage';
import { AIAnalystPage } from './pages/AIAnalystPage';

// Initialize React Query client with optimized settings for trading app
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,                    // Retry failed requests once
      refetchOnWindowFocus: false, // Don't refetch when window regains focus (prevents excessive API calls)
    },
  },
});

/**
 * Protected Route Guard Component
 * 
 * Wraps components that require authentication. Redirects unauthenticated
 * users to login page and shows loading spinner during auth check.
 * 
 * @param children - The protected component(s) to render when authenticated
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking authentication status
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Redirect to login if user is not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Render protected content for authenticated users
  return <>{children}</>;
}

/**
 * Public Route Guard Component
 * 
 * Wraps authentication pages (login, register) that should only be accessible
 * to unauthenticated users. Redirects authenticated users to dashboard.
 * 
 * @param children - The public component(s) to render when not authenticated
 */
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking authentication status
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Redirect authenticated users to dashboard (prevents accessing login/register)
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Render public content for unauthenticated users
  return <>{children}</>;
}

/**
 * Application Routes Component
 * 
 * Defines all application routes with appropriate guards. Must be rendered
 * inside AuthProvider to access authentication context.
 * 
 * Route structure:
 * - Public routes: /login, /register (redirect if authenticated)
 * - Protected routes: /dashboard, /positions (require authentication)
 * - Root and catch-all: redirect to dashboard
 */
function AppRoutes() {
  return (
    <Routes>
      {/* Public authentication routes - only accessible when not logged in */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      
      {/* Protected application routes - require valid authentication */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="positions" element={<PositionsPage />} />
        <Route path="trading" element={<AlgorithmTradingPage />} />
        <Route path="settings" element={<Settings />} />
        <Route path="debug" element={<DebugPage />} />
        <Route path="webhook-stocks" element={<WebhookStocksPage />} />
        <Route path="news" element={<NewsPage />} />
        <Route path="nuke" element={<NukePage />} />
        <Route path="ai-analyst" element={<AIAnalystPage />} />
        {/* Root path - redirect to main dashboard */}
        <Route index element={<Navigate to="/dashboard" replace />} />
      </Route>
      
      {/* Catch-all route - redirect any unmatched paths to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

/**
 * Main App Component
 * 
 * Provides the complete application setup with proper provider hierarchy:
 * 1. QueryClientProvider - Enables React Query throughout the app
 * 2. AuthProvider - Provides authentication state and methods
 * 3. Router - Enables client-side routing
 * 4. AppRoutes - Renders the actual route components
 */
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        {/* ModeThemeProvider wraps AuthProvider so the binder (inside AuthProvider) can
            push user.activeMode into the provider's state via a useEffect. */}
        <ModeThemeProvider>
          <AuthProvider>
            <ModeThemeBinder />
            <Router>
              <AppRoutes />
            </Router>
          </AuthProvider>
        </ModeThemeProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}